<template>
  <b-row>
    <b-col>
      <b-row v-if="(hideUploadWhenImage && !url) || !hideUploadWhenImage">
        <b-col>
          <b-form-group>
            <b-form-file
              v-model="file"
              :disabled="busy || disabled"
              :placeholder="trans('upload_image')"
              :drop-placeholder="trans('drop_file_here')"
              :browse-text="trans('browse')"
              accept="image/jpeg, image/png, image/gif"
              @input="onImageFileChange"
            />
          </b-form-group>
          <small v-if="!!help" class="d-block text-muted">
            <i class="fa fa-fw fa-info-circle"></i>
            {{ help }}
          </small>
        </b-col>
        <b-col class="col-auto pl-0">
          <b-button :variant="show_media ? 'danger' : 'primary'" @click="show_media = !show_media" :disabled="busy || disabled && !show_media">
            {{ trans(show_media ? 'cancel' : 'media') }}
          </b-button>
        </b-col>
      </b-row>
      <b-form-group v-if="!onlyImage" :label="trans('image_alt')">
        <b-form-textarea v-model="alt" :placeholder="trans('image_alt')" @input="$emit('image_alt', alt)" />
        <b-button variant="link" :disabled="loadingAiImage" class="text-dark p-0 float-right" @click="onRobotClick" v-b-tooltip.hover :title="trans('generate_image_with_ai_desc')">
          <i :class="`fa fa-fw ${loadingAiImage ? 'fa-circle-notch fa-spin' : 'fa-robot'}`"></i>
          <small class="d-none d-lg-inline">{{ trans('generate_image_with_ai') }}</small>
        </b-button>
      </b-form-group>
      <b-form-group v-if="!onlyImage && !hideImageCta" :label="trans('image_cta_url')">
        <b-form-input v-model="cta_url" placeholder="https://example.com" :state="catUrlState" @input="$emit('image_cta_url', cta_url)" />
        <b-form-text v-if="catUrlError" class="text-danger">
          {{ catUrlError }}
        </b-form-text>
      </b-form-group>
      <b-form-group v-if="!onlyImage && !hideImagePercent" :label="trans(type == 'card' ? 'width' : 'image_percent')">
        <b-form-radio-group v-model="percent" :options="percentOptions" @input="$emit('image_percent', percent)" />
      </b-form-group>
      <b-form-group v-if="!hideAlign && !onlyImage && !hideImagePercent" :label="trans('align')">
        <b-form-radio-group v-model="align" :options="alignOptions" />
      </b-form-group>
      <b-form-group v-if="showClear && url">
        <b-button variant="link" class="text-danger px-0" @click="url = null; $emit('image_url', null); $emit('image_media_id', null)">
          <i class="fa fa-fw fa-times"></i>
          {{ trans('remove_image') }}
        </b-button>
      </b-form-group>
    </b-col>
    <b-col class="text-center align-self-center" v-if="!hideImagePreview && !show_media">
      <p class="lead text-center" v-if="busy">
        <i class="fa fa-fw fa-circle-notch fa-spin"></i>
        {{ trans('uploading') }}
      </p>
      <b-img v-if="!busy" fluid :src="url || imagePlaceholderUrl" @error="onImageLoadError" />
      <p v-if="!busy && !isFormBgImage && ((hideUploadWhenImage && !url) || !hideUploadWhenImage)" class="mt-2">
        <b-dropdown v-if="!!canvaApi" text="Canva" variant="canva" size="sm" toggle-class="py-0" right>
          <b-dropdown-header>
            {{ trans('create_in_canva') }}
          </b-dropdown-header>
          <b-dropdown-item @click="onClickCreateInCanva(1200, 200)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_super_thin') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCreateInCanva(1200, 400)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_thin') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCreateInCanva(1200, 600)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_wide') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCreateInCanva(1200, 900)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_landscape') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCreateInCanva(1200, 1200)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_square') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onClickCreateInCanva(1200, 1800)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('image_size_portrait') }}
          </b-dropdown-item>
          <b-dropdown-divider v-if="canva_id"></b-dropdown-divider>
          <b-dropdown-item v-if="canva_id" @click="onClickModifyInCanva">
            <i class="fa fa-fw fa-edit"></i>
            {{ trans('edit_copy_in_canva') }}
          </b-dropdown-item>
        </b-dropdown>
        <b-button v-if="!!media_id" variant="outline-primary" size="sm" class="py-0" @click="onClickCrop">
          <i v-if="!crop_busy" class="fa fa-fw fa-copy"></i>
          <i v-if="crop_busy" class="fa fa-fw fa-circle-notch fa-spin"></i>
          {{ trans('copy') }}
          ({{ trans('crop_resize').toLowerCase() }})
        </b-button>
      </p>
      <p v-if="!busy && isFormBgImage && !!canvaApi">
        <b-button variant="canva" size="sm" class="py-0 mt-2" @click="onClickCreateInCanva(2000, 1200)">
            <i class="fa fa-fw fa-image"></i>
            {{ trans('create_in_canva') }}
        </b-button>
      </p>
    </b-col>
    <b-col v-if="show_media">
      <b-tabs pills v-model="tab">
        <b-tab :title="trans('your_images')" lazy>
          <images-table
            :organization="organization"
            :per-page="5"
            hide-upload
            image-name-only
            no-open
            @choose="onChooseImage"
            class="mt-2"
          ></images-table>
        </b-tab>
        <b-tab title="Pexels" lazy>
          <pexels-table
            :organization="organization"
            :per-page="5"
            :search="pexels"
            image-name-only
            no-open
            @choose="onChooseImage"
            @search="pexels = $event"
            class="mt-2"
          ></pexels-table>
        </b-tab>
      </b-tabs v-model="tab">
    </b-col>
    <image-crop-modal
      :id="cropModalId"
      :organization="organization"
      :image="image_to_crop"
      :max-width="isFormBgImage ? 2000 : undefined"
      @created="onChooseImage"
      @hidden="file = null"
    ></image-crop-modal>
  </b-row>
</template>

<script>
  import ImagesTable from './../media/ImagesTable'
  import PexelsTable from './../media/PexelsTable'
  import ImageCropModal from './../media/ImageCropModal'

  export default {
    props: {
      organization: Object,
      type: {
        type: String,
        default: 'hero',
      },
      image_url: String,
      image_media_id: Number,
      image_alt: String,
      image_cta_url: String,
      image_percent: {
        type: Number,
        default: 0.3333,
      },
      image_pexels: String,
      meta: Object,
      onlyImage: {
        type: Boolean,
        default: false,
      },
      hideUploadWhenImage: {
        type: Boolean,
        default: false,
      },
      showClear: {
        type: Boolean,
        default: false,
      },
      hideImagePreview: {
        type: Boolean,
        default: false,
      },
      hideImagePercent: {
        type: Boolean,
        default: false,
      },
      hideImageCta: {
        type: Boolean,
        default: false,
      },
      hideAlign: {
        type: Boolean,
        default: false,
      },
      percentOptions: {
        type: Array,
        default() {
          return [{
            text: '33%',
            value: 0.3333,
          }, {
            text: '50%',
            value: 0.5,
          }, {
            text: '66%',
            value: 0.6666,
          }]
        },
      },
      modalPostfix: {
        type: String,
        default: 'new',
      },
      isFormBgImage: {
        type: Boolean,
        default: false,
      },
      help: {
        type: String,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      returnCompressedImage: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      ImagesTable,
      PexelsTable,
      ImageCropModal,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        error: null,
        file: null,
        url: this.image_url,
        media_id: this.image_media_id,
        alt: this.image_alt,
        cta_url: this.image_cta_url,
        percent: this.image_percent,
        pexels: this.image_pexels,
        canva_id: this.meta ? (this.meta.canva_id || null) : null,
        align: this.meta ? this.meta.align || 'top' : 'top',
        alignOptions: [
          {value: 'top', text: trans('top')},
          {value: 'middle', text: trans('middle')},
          {value: 'bottom', text: trans('bottom')},
        ],
        show_media: false,
        tab: !!this.image_url &&
          (this.image_url.indexOf('https://images.pexels.com') === 0 || !!this.image_pexels) ? 1 : 0,
        canvaApi: null,
        image_to_crop: null,
        crop_busy: false,
        loadingAiImage: false,
        imageRetryCount: null,
      }
    },
    computed: {
      imagePlaceholderUrl() {
        if (this.isFormBgImage) return '/img/frontend/blocks/2000x1200.png'
        if (this.type == 'card') return '/img/frontend/blocks/540x270.png'
        return this.type == 'hero' ?
          '/img/frontend/blocks/1200x600.png' :
          '/img/frontend/blocks/270.png'
      },
      cropModalId() {
        return `image-crop-modal-${this.modalPostfix}`
      },
      catUrlState() {
        if (!this.cta_url) return null
        return _.validateUrl(this.cta_url)
      },
      catUrlError() {
        if (this.catUrlState !== false) return null
        let error = trans('invalid_url')
        if (this.cta_url.trim().indexOf('http://') !== 0 && this.cta_url.trim().indexOf('https://') !== 0) error += ' ' + trans('url_missing_http')
        return error
      },
    },
    watch: {
      image_url() {
        this.url = this.image_url
      },
      image_media_id() {
        this.media_id = this.image_media_id
      },
      image_alt() {
        this.alt = this.image_alt
      },
      image_cta_url() {
        this.cta_url = this.image_cta_url
      },
      image_percent() {
        this.percent = this.image_percent
      },
      image_pexels() {
        this.pexels = this.image_pexels
      },
      meta() {
        let canva_id = this.meta ? (this.meta.canva_id || null) : null
        if (this.canva_id != canva_id) this.canva_id = canva_id
        let align = this.meta ? this.meta.align || 'top' : 'top'
        if (this.align != align) this.align = align
      },
      align() {
        let meta = {
          canva_id: this.canva_id || null,
          align: this.align || 'top',
        }
        if (!_.isEqual(this.meta, meta)) {
          this.$emit('meta', meta)
        }
      },
    },
    methods: {
      onClickCrop() {
        this.crop_busy = true
        this.error = null
        const url = `/organization/${this.organization.id}/media/${this.media_id}`
        axios.get(url)
        .then(response => {
          this.imageRetryCount = null
          this.crop_busy = false
          this.image_to_crop = response.data
          this.$bvModal.show(this.cropModalId)
        })
        .catch(this.handleError)
      },
      onClickCreateInCanva(width, height) {
        this.canvaApi.createDesign({
          design: {
            type: 'EmailHeader',
            dimensions: {
              units: 'px',
              width: width,
              height: height,
            },
          },
          editor: {
            publishLabel: trans('save'),
            fileType: 'png',
          },
          onDesignOpen: ({designId}) => {
            // Triggered when editor finishes loading and opens a new design.
            // You can save designId for future use.
            console.log('onDesignOpen', designId)
          },
          onDesignPublish: ({exportUrl, designId}) => {
            // Triggered when design is published to an image.
            // Save the image to your server as the exportUrl will expire shortly.
            console.log('onDesignPublish', exportUrl, designId)
            this.onPublishNewCanvaImage(exportUrl, designId)
          },
          onDesignClose: () => {
            // Triggered when editor is closed.
            console.log('onDesignClose')
          },
        })
      },
      onClickModifyInCanva() {
        this.canvaApi.editDesign({
          design: {
            id: this.canva_id,
          },
          editor: {
            publishLabel: trans('save'),
            fileType: 'png',
          },
          onDesignOpen: ({designId}) => {
            // Triggered when editor finishes loading and opens a new design.
            // You can save designId for future use.
            console.log('onDesignOpen', designId)
          },
          onDesignPublish: ({exportUrl, designId}) => {
            // Triggered when design is published to an image.
            // Save the image to your server as the exportUrl will expire shortly.
            console.log('onDesignPublish', exportUrl, designId)
            this.onPublishNewCanvaImage(exportUrl, designId)
          },
          onDesignClose: () => {
            // Triggered when editor is closed.
            console.log('onDesignClose')
          },
        })
      },
      onPublishNewCanvaImage(canva_url, canva_id) {
        this.busy = true
        this.error = null
        const url = `/organization/${this.organization.id}/media/image/canva`
        axios.post(url, {
          canva_url: canva_url,
          canva_id: canva_id,
        })
        .then(response => {
          this.busy = false
          this.onChooseImage(response.data)
        })
        .catch(this.handleError)
      },
      onChooseImage(image) {
        this.url = image.url
        this.media_id = image.id || null
        this.show_media = false
        let pexels = this.url.indexOf('https://images.pexels.com') === 0 ? this.pexels : null
        this.$emit('image_pexels', pexels)
        this.$emit('image_url', this.returnCompressedImage ? image.w600_url : this.url)
        this.$emit('image_media_id', image.id || null)
        this.canva_id = image.canva_id
        this.$emit('meta', this.canva_id ? {
          canva_id: this.canva_id,
          align: this.align || 'top',
        } : {})
      },
      onImageFileChange() {
        if (!this.file) return
        this.imageRetryCount = null
        var reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = (e) => {
          this.image_to_crop = {
            file_name: this.file.name,
            data_url: e.target.result,
            size: this.file.size,
            mime_type: this.file.type,
            file: this.file
          }
          this.busy = false
          this.$bvModal.show(this.cropModalId)
        }
      },
      handleError(error) {
        this.busy = false
        this.error = _.errorFromAjax(error)
        this.$bvToast.toast(this.error, {
          title: trans('error'),
          variant: 'danger',
          solid: true,
        })
      },
      onRobotClick() {
        if (!this.alt) return
        this.loadingAiImage = true
        const url = `/organization/${this.organization.id}/ai_image`
        axios.get(url, {
          params: {
            prompt: this.alt,
          }
        }).then((res) => {
          this.imageRetryCount = null
          this.loadingAiImage = false
          this.onChooseImage(res.data)
        }).catch((error) => {
          this.loadingAiImage = false
          this.error = _.errorFromAjax(error)
          this.$bvToast.toast(this.error, {
            title: trans('error'),
            variant: 'danger',
            solid: true,
          })
        })
      },
      onImageLoadError(e) {
        if (!this.imageRetryCount) this.imageRetryCount = 0;
        if (this.imageRetryCount < 5) {
          this.imageRetryCount++;
          setTimeout(() => {
            const oldUrl = this.url;
            this.url = '';
            this.$nextTick(() => {
              this.url = oldUrl;
            });
          }, 1500);
        }
      },
    },
    mounted() {
      window.blockImageTabComponent = this
      _.loadCanva(canvaApi => {
        this.canvaApi = canvaApi
      })
    },
  }
</script>